import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { DOCTOR_INFO_DEFAULT_ID } from '../../tasks/slice/taskListSlice';
import AllDoctors from './AllDoctors';
import ApsCandidates from './ApsCandidates';
import { prepareAllDoctorsRows } from './deduplicateDoctors';
import './DoctorInfo.scss';
import { fetchDoctors } from './fetchDoctors';
import { DisclosureDoctor, DoctorRow, DxClaim } from './types';

interface DoctorInfoProps {
  applicationId: string;
  product: string;
  carrier: string;
  policyId: number;
}

type TopView = 'aps' | 'all';

export default function DoctorInfo({ applicationId }: DoctorInfoProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [topView, setTopView] = useState<TopView>('aps');
  const [dxClaims, setDxClaims] = useState<DxClaim[]>([]);
  const [rxRows, setRxRows] = useState<DoctorRow[]>([]);
  const [disclosure, setDisclosure] = useState<DisclosureDoctor[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchDoctors(applicationId)
      .then(result => {
        if (cancelled) return;
        setDxClaims(result.dx.claims);
        setRxRows(result.rx);
        setDisclosure(result.disclosure);
      })
      .catch(error => {
        console.error('Error fetching doctor info:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  const allDoctorsRows = useMemo(
    () => prepareAllDoctorsRows(dxClaims, rxRows),
    [dxClaims, rxRows],
  );

  return (
    <div data-section-id={'Doctor Info'} id={String(DOCTOR_INFO_DEFAULT_ID)}>
      <Accordion sx={{ boxShadow: 'none', p: 0, m: 0 }} expanded={isExpanded}>
        <AccordionSummary
          className="doctor-info-accordion-summary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            p: 0,
            m: 0,
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="rotate-icon-container">
            <IconButton size="small">
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={isExpanded ? 90 : undefined}
              />
            </IconButton>
          </div>
          <Typography variant="h2">Doctor Info</Typography>
        </AccordionSummary>

        <div className="doctor-info-panel">
          <div className="panel-header">
            <span className="panel-title">Doctor Info</span>
            <div className="view-toggle" role="tablist">
              <button
                type="button"
                className={`view-toggle-btn ${
                  topView === 'aps' ? 'active' : ''
                }`}
                onClick={() => setTopView('aps')}
              >
                APS Candidates
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${
                  topView === 'all' ? 'active' : ''
                }`}
                onClick={() => setTopView('all')}
              >
                All Doctors (
                {allDoctorsRows.dxRows.length + allDoctorsRows.rxRows.length})
              </button>
            </div>
          </div>

          {topView === 'aps' ? (
            <ApsCandidates dxClaims={dxClaims} disclosure={disclosure} />
          ) : (
            <AllDoctors
              dxRows={allDoctorsRows.dxRows}
              rxRows={allDoctorsRows.rxRows}
            />
          )}
        </div>
        <AccordionDetails sx={{ p: '0px' }}></AccordionDetails>
      </Accordion>
    </div>
  );
}
